'use client';

import { forwardRef } from 'react';
import { Accordion as MantineAccordion } from '@mantine/core';
import type {
  AccordionControlProps as MantineAccordionControlProps,
  AccordionItemProps as MantineAccordionItemProps,
  AccordionPanelProps as MantineAccordionPanelProps,
  AccordionProps as MantineAccordionProps,
} from '@mantine/core';

import type { LockedProps } from '../types';

// Fully locked: no per-slot `classNames`/`className`/`style` escape hatches (unlike Tabs, which
// keeps `classNames`). Style via the built-in `variant` (default/contained/filled/separated) plus
// `radius`/`chevronPosition`. For a disclosure that renders its own header/card chrome, use a
// toggle button + `<Collapse>` rather than re-skinning this component.
type AccordionProps<Multiple extends boolean> = Omit<MantineAccordionProps<Multiple>, LockedProps>;

export const Accordion = <Multiple extends boolean>(props: AccordionProps<Multiple>) => {
  return <MantineAccordion {...props} />;
};

export const AccordionItem = (props: Omit<MantineAccordionItemProps, LockedProps>) => (
  <MantineAccordion.Item {...props} />
);

type AccordionControlProps = Omit<MantineAccordionControlProps, LockedProps>;

export const AccordionControl = forwardRef<HTMLButtonElement, AccordionControlProps>(
  (props, ref) => {
    return <MantineAccordion.Control {...props} ref={ref} />;
  },
);

AccordionControl.displayName = 'AccordionControl';

export const AccordionPanel = (props: Omit<MantineAccordionPanelProps, LockedProps>) => (
  <MantineAccordion.Panel {...props} />
);
